<template>
    <section class="news-modern section-space">
        <div class="pbb-container">
            <header class="news-modern__header">
                <div>
                    <span class="eyebrow">Informação atualizada</span>
                    <h2>Últimas publicações</h2>
                </div>
                <div class="news-modern__header-aside">
                    <p>
                        Conteúdos, comunicados e orientações para acompanhar os processos com mais
                        clareza.
                    </p>
                    <router-link to="/categoria/noticias" class="news-modern__all pbb-focus">
                        Ver todas as publicações
                        <v-icon size="19">mdi-arrow-right</v-icon>
                    </router-link>
                </div>
            </header>

            <div v-if="loading" class="news-modern__loading" aria-live="polite">
                <v-progress-circular indeterminate color="secondary" size="28" width="2" />
                <span>Carregando publicações</span>
            </div>

            <div v-else-if="latestNews" class="news-modern__editorial">
                <router-link
                    :to="newsRoute(latestNews)"
                    class="news-feature pbb-focus"
                    :style="featureBackground"
                >
                    <span class="news-feature__index" aria-hidden="true">01</span>
                    <div class="news-feature__content">
                        <span class="news-feature__meta">
                            {{ categoryName(latestNews) }}
                            <span aria-hidden="true">·</span>
                            {{ formatDate(latestNews.created_at) }}
                        </span>
                        <h3>{{ latestNews.title }}</h3>
                        <p>{{ excerpt(latestNews.content) }}</p>
                        <span class="news-feature__action">
                            Ler publicação
                            <v-icon size="21">mdi-arrow-top-right</v-icon>
                        </span>
                    </div>
                </router-link>

                <div class="news-modern__list">
                    <span class="news-modern__list-label">Mais recentes</span>
                    <router-link
                        v-for="(news, index) in recentNews"
                        :key="news.id"
                        :to="newsRoute(news)"
                        class="news-row pbb-focus"
                    >
                        <span class="news-row__index">{{ formatIndex(index + 2) }}</span>
                        <span class="news-row__content">
                            <small>{{ formatDate(news.created_at) }}</small>
                            <strong>{{ news.title }}</strong>
                        </span>
                        <span class="news-row__arrow">
                            <v-icon size="20">mdi-arrow-top-right</v-icon>
                        </span>
                    </router-link>
                </div>
            </div>

            <div v-else class="news-modern__empty">
                <span class="news-modern__empty-icon">
                    <v-icon color="secondary" size="26">mdi-newspaper-variant-outline</v-icon>
                </span>
                <div>
                    <strong>Publicações e comunicados oficiais</strong>
                    <p>Acompanhe neste espaço as informações mais recentes da PB&amp;B.</p>
                </div>
                <router-link to="/categoria/noticias" class="news-modern__all pbb-focus">
                    Acessar publicações
                    <v-icon size="19">mdi-arrow-right</v-icon>
                </router-link>
            </div>
        </div>
    </section>
</template>

<script>
import axios from 'axios';

export default {
    name: 'NewsSections',
    data: () => ({
        latestNews: null,
        recentNews: [],
        loading: false
    }),
    computed: {
        featureBackground() {
            if (!this.latestNews || !this.latestNews.cover_image_url) return {};
            return {
                backgroundImage: `linear-gradient(115deg, rgba(0, 28, 49, .96), rgba(1, 42, 74, .76)), url(${this.latestNews.cover_image_url})`
            };
        }
    },
    created() {
        this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            try {
                const response = await axios.get('/api/posts', { params: { limit: 5 } });
                const data = response.data.content.data || [];
                const sortedData = [...data].sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                );

                this.latestNews = sortedData[0] || null;
                this.recentNews = sortedData.slice(1, 5);
            } catch (error) {
                this.latestNews = null;
                this.recentNews = [];
            } finally {
                this.loading = false;
            }
        },
        newsRoute(news) {
            return `/categoria/noticias/${news.id}`;
        },
        categoryName(news) {
            return (news.category && news.category.name) || 'Publicação';
        },
        formatDate(date) {
            return date ? this.$moment(date).format('DD.MM.YYYY') : 'PB&B';
        },
        formatIndex(index) {
            return String(index).padStart(2, '0');
        },
        excerpt(content) {
            const document = new DOMParser().parseFromString(content || '', 'text/html');
            return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
        }
    }
};
</script>

<style scoped lang="scss">
.news-modern {
    overflow: hidden;
    background: #eef3f5;
}
.news-modern__header {
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(280px, 0.7fr);
    gap: 72px;
    align-items: end;
    margin-bottom: 52px;
}
.news-modern__header h2 {
    margin: 18px 0 0;
    color: var(--pbb-navy);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: clamp(42px, 5vw, 66px);
    font-weight: 400;
    line-height: 1.02;
    letter-spacing: -0.055em;
}
.news-modern__header-aside {
    padding-bottom: 6px;
}
.news-modern__header-aside p {
    max-width: 390px;
    margin: 0 0 22px;
    color: var(--pbb-muted);
    font-size: 14px;
    line-height: 1.7;
}
.news-modern__all {
    display: inline-flex;
    gap: 12px;
    align-items: center;
    color: var(--pbb-navy);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.055em;
    text-decoration: none;
    text-transform: uppercase;
}
.news-modern__all .v-icon {
    color: var(--pbb-copper);
    transition: transform 180ms ease;
}
.news-modern__all:hover .v-icon {
    transform: translateX(5px);
}
.news-modern__editorial {
    display: grid;
    grid-template-columns: minmax(0, 1.12fr) minmax(370px, 0.88fr);
    overflow: hidden;
    border: 1px solid rgba(1, 42, 74, 0.12);
    border-radius: 10px;
    background: #fff;
}
.news-feature {
    position: relative;
    display: flex;
    min-height: 450px;
    overflow: hidden;
    padding: clamp(34px, 4vw, 58px);
    background-color: var(--pbb-navy);
    background-position: center;
    background-size: cover;
    color: #fff;
    text-decoration: none;
    isolation: isolate;
}
.news-feature::before {
    position: absolute;
    right: -70px;
    bottom: -170px;
    width: 390px;
    height: 390px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    box-shadow: 0 0 0 58px rgba(255, 255, 255, 0.025), 0 0 0 116px rgba(255, 255, 255, 0.018);
    content: '';
    transition: transform 700ms cubic-bezier(0.22, 1, 0.36, 1);
}
.news-feature:hover::before {
    transform: scale(1.08) translate(-8px, -8px);
}
.news-feature__index {
    position: absolute;
    top: 28px;
    right: 34px;
    color: rgba(255, 255, 255, 0.1);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 82px;
    font-weight: 300;
    line-height: 1;
}
.news-feature__content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    max-width: 610px;
}
.news-feature__meta {
    display: inline-flex;
    gap: 9px;
    align-items: center;
    color: #e5a476;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
}
.news-feature h3 {
    margin: auto 0 22px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: clamp(30px, 3.2vw, 45px);
    font-weight: 400;
    line-height: 1.12;
    letter-spacing: -0.045em;
}
.news-feature p {
    display: -webkit-box;
    max-width: 570px;
    margin: 0;
    overflow: hidden;
    color: rgba(255, 255, 255, 0.7);
    font-size: 13px;
    line-height: 1.75;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}
.news-feature__action {
    display: inline-flex;
    gap: 14px;
    align-items: center;
    margin-top: 32px;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}
.news-feature__action .v-icon {
    color: #e5a476;
    transition: transform 180ms ease;
}
.news-feature:hover .news-feature__action .v-icon {
    transform: translate(4px, -4px);
}
.news-modern__list {
    display: flex;
    flex-direction: column;
    padding: 28px clamp(26px, 3vw, 42px) 18px;
}
.news-modern__list-label {
    padding: 0 0 14px 42px;
    color: #929ba1;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}
.news-row {
    display: grid;
    grid-template-columns: 28px minmax(0, 1fr) 34px;
    gap: 14px;
    align-items: center;
    flex: 1;
    min-height: 92px;
    border-top: 1px solid #e3e6e7;
    color: var(--pbb-navy);
    text-decoration: none;
}
.news-row__index {
    color: var(--pbb-copper);
    font-size: 10px;
    font-weight: 700;
}
.news-row__content {
    display: flex;
    flex-direction: column;
    gap: 7px;
}
.news-row__content small {
    color: #87929a;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 0.07em;
}
.news-row__content strong {
    display: -webkit-box;
    overflow: hidden;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.35;
    letter-spacing: -0.025em;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
}
.news-row__arrow {
    display: grid;
    width: 32px;
    height: 32px;
    place-items: center;
    border: 1px solid #d9dfe1;
    border-radius: 50%;
    transition: border-color 180ms ease, background-color 180ms ease;
}
.news-row__arrow .v-icon {
    color: var(--pbb-navy);
    transition: transform 180ms ease;
}
.news-row:hover .news-row__arrow {
    border-color: var(--pbb-copper);
    background: #fbf4ee;
}
.news-row:hover .news-row__arrow .v-icon {
    transform: translate(2px, -2px);
}
.news-modern__loading,
.news-modern__empty {
    display: flex;
    gap: 20px;
    align-items: center;
    min-height: 150px;
    padding: 32px;
    border: 1px solid #dde3e5;
    border-radius: 10px;
    background: #fff;
    color: var(--pbb-muted);
}
.news-modern__empty-icon {
    display: grid;
    width: 48px;
    height: 48px;
    flex: 0 0 48px;
    place-items: center;
    border-radius: 50%;
    background: #fbf4ee;
}
.news-modern__empty strong {
    color: var(--pbb-navy);
}
.news-modern__empty p {
    margin: 5px 0 0;
    font-size: 13px;
}
.news-modern__empty .news-modern__all {
    margin-left: auto;
}
@media (max-width: 960px) {
    .news-modern__header {
        grid-template-columns: 1fr;
        gap: 22px;
        margin-bottom: 38px;
    }
    .news-modern__header-aside p {
        max-width: 620px;
    }
    .news-modern__editorial {
        grid-template-columns: 1fr;
    }
    .news-feature {
        min-height: 390px;
    }
    .news-modern__list-label {
        padding-top: 6px;
    }
}
@media (max-width: 600px) {
    .news-modern__header h2 {
        font-size: 41px;
    }
    .news-feature {
        min-height: 400px;
        padding: 30px 26px;
    }
    .news-feature__index {
        top: 24px;
        right: 24px;
        font-size: 60px;
    }
    .news-feature h3 {
        font-size: 31px;
    }
    .news-modern__list {
        padding: 24px 22px 10px;
    }
    .news-row {
        min-height: 108px;
    }
    .news-modern__empty {
        align-items: flex-start;
        flex-wrap: wrap;
    }
    .news-modern__empty .news-modern__all {
        width: 100%;
        margin-left: 68px;
    }
}
@media (prefers-reduced-motion: reduce) {
    .news-feature::before,
    .news-feature__action .v-icon,
    .news-row__arrow,
    .news-row__arrow .v-icon,
    .news-modern__all .v-icon {
        transition: none;
    }
}
</style>
